'use client';

import React from 'react';
import Link from 'next/link';
import { Avatar, Box, Button, ButtonProps, styled, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import { paths } from '@/paths';

import CopyButton from './copy-button';

interface OperatorDetails {
  opTitle: string;
  opId: string;
  parentOpTitle?: string;
  parentOpId?: string;
  avatar?: string;
}

const Root = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  maxWidth: '100%',
  gap: '8px',
});

const InfoColumn = styled(Stack)({
  flexDirection: 'column',
  gap: 2,
});

const OperatorLink = styled((props: ButtonProps) => <Button {...props} />)(() => ({
  display: 'block',
  whiteSpace: 'nowrap',
  padding: '0 !important',
  maxWidth: '100%',
  textAlign: 'start',
  textTransform: 'none',
}));
const TextWithCopy = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  maxWidth: '100%',
  position: 'relative',
  '&:hover .copy-button': {
    opacity: 1,
    pointerEvents: 'auto',
  },
});

const CopyButtonWrapper = styled('div')({
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 0.3s ease',
});

const EllipsisText = styled(Typography)({
  textAlign: 'start',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
});

const SecondaryText = styled(Typography)({
  textAlign: 'start',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
});

export function OperatorCell(operator: OperatorDetails): React.JSX.Element {
  const opIdLogo = `${process.env.NEXT_PUBLIC_CDN_URL}/images/op-avatar/${operator.opId}`;

  const hasParent = Boolean(operator.parentOpTitle);

  const titleText = hasParent ? `${operator.opId} - ${operator.opTitle}` : operator.opTitle;

  const subText = hasParent ? `${operator.parentOpId} - ${operator.parentOpTitle}` : operator.opId;

  return (
    <Root>
      <Avatar src={opIdLogo} />
      <InfoColumn>
        <TextWithCopy>
          <OperatorLink component={Link} href={paths.dashboard.operators.details(operator.opId)} color="inherit">
            <EllipsisText variant="body2">{titleText}</EllipsisText>
          </OperatorLink>
          <CopyButtonWrapper className="copy-button">
            <CopyButton text={titleText} />
          </CopyButtonWrapper>
        </TextWithCopy>

        <TextWithCopy>
          <SecondaryText color="text.secondary" variant="body2">
            {subText}
          </SecondaryText>
          <CopyButtonWrapper className="copy-button">
            <CopyButton text={subText} />
          </CopyButtonWrapper>
        </TextWithCopy>
      </InfoColumn>
    </Root>
  );
}
